import { useState } from "react";
import { Link } from "react-router-dom";
import { GripVertical, Clapperboard, EyeOff, Eye, Trash2, Pencil } from "lucide-react";
import { fmtNum } from "@/lib/studio";

let dragging = null;

const ChapterCard = ({ it, n, editable, link, onToggleHidden, onDelete }) => (
  <Link to={link} className="block h-full rounded-md border border-[#2D3442] bg-[#1A202C] p-4 transition-colors hover:border-[#D97706]/60" data-testid={`board-card-${it.id}`}>
    <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.16em] text-[#F59E0B]">
      <span data-testid={`board-number-${it.id}`}>Chapter {n}</span>
      {it.hidden && <span className="flex items-center gap-1 text-[#A8A090]" data-testid={`board-hidden-${it.id}`}><EyeOff className="h-3 w-3" />Hidden</span>}
    </div>
    <div className={`mt-2 font-display text-xl leading-tight [overflow-wrap:anywhere] ${it.title ? "text-[#F3EBDD]" : "italic text-[#A8A090]"}`} data-testid={`board-title-${it.id}`}>{it.title || "Untitled chapter"}</div>
    {it.summary && <p className="mt-1 line-clamp-3 text-xs text-[#A8A090]">{it.summary}</p>}
    <div className="mt-3 flex items-center justify-between text-[11px] text-[#A8A090]">
      <span data-testid={`board-words-${it.id}`}>{fmtNum(it.word_count)} words</span>
      {editable && (
        <span className="flex gap-2" onClick={(e) => e.preventDefault()}>
          <button onClick={() => onToggleHidden(it)} title={it.hidden ? "Show to team" : "Hide from team"} className="hover:text-[#F3EBDD]" data-testid={`board-toggle-hidden-${it.id}`}>{it.hidden ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}</button>
          <button onClick={() => onDelete(it)} className="hover:text-[#F87171]" data-testid={`board-delete-${it.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
        </span>
      )}
    </div>
  </Link>
);

const SceneCard = ({ it, editable, onEditScene, onDelete }) => (
  <div className="h-full rounded-md border border-dashed border-[#8B5CF6]/60 bg-[#13171F] p-4" data-testid={`board-card-${it.id}`}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-[#C4B5FD]"><Clapperboard className="h-3 w-3" />Scene placeholder</div>
    <div className="mt-2 font-display text-xl leading-tight text-[#F3EBDD] [overflow-wrap:anywhere]">{it.title}</div>
    {it.summary && <p className="mt-1 line-clamp-3 text-xs text-[#A8A090]">{it.summary}</p>}
    <div className="mt-3 flex items-center justify-between text-[11px] text-[#A8A090]">
      <span>Not counted in words</span>
      {editable && (
        <span className="flex gap-2">
          {!it.missing && <button onClick={() => onEditScene(it.scene_id)} className="hover:text-[#F3EBDD]" data-testid={`board-edit-scene-${it.id}`}><Pencil className="h-3.5 w-3.5" /></button>}
          <button onClick={() => onDelete(it)} className="hover:text-[#F87171]" data-testid={`board-delete-${it.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
        </span>
      )}
    </div>
  </div>
);

export const StoryBoard = ({ items, editable, chapterLink, onReorder, ...actions }) => {
  const [over, setOver] = useState(null);
  const drop = (to) => {
    const from = dragging;
    dragging = null;
    setOver(null);
    if (from == null || from === to) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onReorder(next);
  };
  let n = 0;
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4" data-testid="storyboard">
      {items.map((it, i) => {
        if (it.kind === "chapter") n += 1;
        return (
          <div key={it.id} draggable={editable} data-testid={`board-slot-${i}`}
            onDragStart={(e) => { dragging = i; e.dataTransfer.effectAllowed = "move"; }}
            onDragOver={(e) => { if (dragging != null) { e.preventDefault(); setOver(i); } }}
            onDragLeave={() => setOver(null)} onDrop={(e) => { e.preventDefault(); drop(i); }} onDragEnd={() => { dragging = null; setOver(null); }}
            className={`relative rounded-md transition-transform ${over === i ? "scale-[1.02] ring-2 ring-[#D97706]/60" : ""}`}>
            {editable && <GripVertical className="absolute -left-0.5 top-1/2 z-10 h-4 w-4 -translate-y-1/2 cursor-grab text-[#A8A090]/50" />}
            {it.kind === "chapter" ? <ChapterCard it={it} n={n} editable={editable} link={chapterLink(it)} {...actions} /> : <SceneCard it={it} editable={editable} {...actions} />}
          </div>
        );
      })}
    </div>
  );
};
